import { MenuItemDef, GridApi, IRowNode, GetContextMenuItemsParams } from '@ag-grid-community/core';
import { WorklistService } from 'src/services/Worklist/Worklist.service';
import { FunctionProps } from 'src/components/ListGridPair/ListGridPair';
import ServiceContainer from 'src/ServiceContainer';
import { toast } from 'react-toastify';
import { isEmpty, isNil } from 'lodash';
import { WorklistApiPayload } from 'src/dao/Worklist.client';
import { XOR } from 'src/types/Primitive';
import { WorklistConfigButtons } from 'configuration-schemas/confdefnView';
const ICON_PREFIX = 'fa fa-fw';

const iconClassToIconElement = (iconClass: string) => {
  const iconElement = window.document.createElement('i');
  iconElement.className = `${ICON_PREFIX} ${iconClass}`;
  return iconElement;
};

export const iconClassToString = (iconClass: string) => {
  return `${ICON_PREFIX} ${iconClass}`;
};

export interface NeededGetContextMenUItemsParams {
  node: Pick<IRowNode, 'data'>;
}

export enum ContextMenuType {
  grid = 'grid',
  card = 'card',
  stylePreview = 'stylePreview',
}
export type ContextMenuCreationParams =
  | {
      parentIdentityField: string;
      type: ContextMenuType.grid;
      openStylePane: FunctionProps['openStylePane'];
      onPostAction?: () => void;
    }
  | {
      parentIdentityField: string;
      type: ContextMenuType.card | ContextMenuType.stylePreview;
      openStylePane: () => void;
      onPostAction?: () => void;
    };

const NO_ITEMS_TEXT = 'No items selected for action.';

interface WorklistPreActions {
  onPreAction?: () => void;
}
type WorklistPostActions = Pick<ContextMenuCreationParams, 'onPostAction'>;

type WorklistAction = {
  buttonAction: (params: string[] | string) => Promise<WorklistApiPayload>;
  extraParams: string[] | string;
};

type WorklistActions = WorklistAction & {
  actions: WorklistPreActions & WorklistPostActions;
  errorMessage: string;
};

const getSelectedItems = (identityField: string, getGridApi: () => GridApi | undefined): string[] => {
  const selectedRows: any = [];
  const gridApi = getGridApi();

  if (isNil(gridApi)) {
    return [];
  }

  gridApi.forEachNodeAfterFilter((node) => {
    if (isNil(node.data)) {
      return; // skip any nodes without data
    }
    if (node.data.exceptionselect) {
      selectedRows.push(node.data);
    } else if (node.isSelected()) {
      selectedRows.push(node.data);
    }
  });

  return selectedRows.map((row: any) => row[identityField]);
};

const runWorklistActions = ({ buttonAction, extraParams, actions, errorMessage }: WorklistActions) => {
  let action;

  if (buttonAction) {
    action = buttonAction(extraParams);
  } else {
    return;
  }

  if (actions.onPreAction) {
    actions.onPreAction();
  }

  action
    .then(() => {
      // TODO: used to refetchWorklist() here, what now
    })
    .catch((e) => {
      toast.error(errorMessage);
      ServiceContainer.loggingService.error(errorMessage, e.stack);
    })
    .finally(() => {
      if (actions.onPostAction) {
        actions.onPostAction();
      }
    });
};

export const getWorklistMenuItems = (
  mode: 'multiselect' | 'singleSelect',
  config: {
    identityField: string;
    getGridApi?: () => GridApi | undefined;
    //use for single select
    singleSelectParams?: ContextMenuCreationParams;
    actions?: WorklistPreActions & WorklistPostActions;
    worklistConfigButtons: WorklistConfigButtons;
    params?: GetContextMenuItemsParams;
    // use for ag-grid
    isContextMenu?: boolean;
  },
): ((MenuItemDef & { hidden?: boolean }) | string)[] => {
  if (mode === 'singleSelect' && config.params) {
    const colDef = config.params.column && config.params.column.getColDef();
    if (colDef?.cellClass !== 'highlight') {
      return [];
    }
  }
  const worklistService = WorklistService();

  const getSelectedOrSingleParam = (): string | string[] => {
    if (mode === 'multiselect' && config.getGridApi) {
      return getSelectedItems(config.identityField, config.getGridApi);
    }
    return config.params?.node ? config.params.node.data[config.identityField || ''] : '';
  };
  const singleActions: WorklistPostActions = {
    onPostAction: config.singleSelectParams?.onPostAction,
  };
  const singleOrMultiAction = () => {
    if (mode === 'multiselect' && config.actions) {
      return { ...config.actions };
    } else {
      return {
        ...singleActions,
      };
    }
  };
  const selectedItems = getSelectedOrSingleParam();
  const noItemsSelected = () => {
    if (mode === 'multiselect' && isEmpty(selectedItems)) {
      toast.warn(NO_ITEMS_TEXT);
      return '';
    }
  };
  const worklistConfigButtonsFinal = config.worklistConfigButtons
    .map((button) => {
      switch (button.type) {
        case 'active':
          const buttonActiveAction: (params: string | string[]) => Promise<WorklistApiPayload> = (params) => {
            return Array.isArray(params)
              ? worklistService.addItemsToWorklist(params) // `params` is `string[]`
              : worklistService.addToWorklist(params); // `params` is `string`
          };
          return {
            name: button.text,
            action() {
              noItemsSelected();
              runWorklistActions({
                buttonAction: buttonActiveAction,
                extraParams: selectedItems,
                actions: singleOrMultiAction(),
                errorMessage: 'An error occurred adding items to the worklist',
              });
            },
            icon: config.isContextMenu ? iconClassToIconElement(button.icon) : iconClassToString(button.icon),
            tooltip: button.tooltip,
          };

        case 'snooze_1d':
          const buttonSnoozeAction: (params: string | string[]) => Promise<WorklistApiPayload> = (params) => {
            return Array.isArray(params)
              ? worklistService.snoozeItemsOneDay(params) // `params` is `string[]`
              : worklistService.snoozeOneDay(params); // `params` is `string`
          };
          return {
            name: button.text,
            action() {
              noItemsSelected();
              runWorklistActions({
                buttonAction: buttonSnoozeAction,
                extraParams: selectedItems,
                actions: singleOrMultiAction(),
                errorMessage: 'An error occurred snoozing items',
              });
            },
            icon: config.isContextMenu ? iconClassToIconElement(button.icon) : iconClassToString(button.icon),
            tooltip: button.tooltip,
          };

        case 'snooze_1w':
          const buttonSnoozeWeekAction: (params: string | string[]) => Promise<WorklistApiPayload> = (params) => {
            return Array.isArray(params)
              ? worklistService.snoozeItemsOneWeek(params) // `params` is `string[]`
              : worklistService.snoozeOneWeek(params); // `params` is `string`
          };
          return {
            name: button.text,
            action() {
              noItemsSelected();
              runWorklistActions({
                buttonAction: buttonSnoozeWeekAction,
                extraParams: selectedItems,
                actions: singleOrMultiAction(),
                errorMessage: 'An error occurred snoozing items',
              });
            },
            icon: config.isContextMenu ? iconClassToIconElement(button.icon) : iconClassToString(button.icon),
            tooltip: button.tooltip,
          };

        case 'reviewed':
          const buttonReviewedAction: (params: string | string[]) => Promise<WorklistApiPayload> = (params) => {
            return Array.isArray(params)
              ? worklistService.markItemsAsReviewed(params) // `params` is `string[]`
              : worklistService.markAsReviewed(params); // `params` is `string`
          };
          return {
            name: button.text,
            action() {
              noItemsSelected();
              runWorklistActions({
                buttonAction: buttonReviewedAction,
                extraParams: selectedItems,
                actions: singleOrMultiAction(),
                errorMessage: 'An error occurred marking items as reviewed',
              });
            },
            icon: config.isContextMenu ? iconClassToIconElement(button.icon) : iconClassToString(button.icon),
            tooltip: button.tooltip,
          };

        case 'dropped':
          const buttonRemoveAction: (params: string | string[]) => Promise<WorklistApiPayload> = (params) => {
            return Array.isArray(params)
              ? worklistService.removeItemsFromWorklist(params) // `params` is `string[]`
              : worklistService.removeFromWorklist(params); // `params` is `string`
          };
          return {
            name: button.text,
            action() {
              noItemsSelected();
              runWorklistActions({
                buttonAction: buttonRemoveAction,
                extraParams: selectedItems,
                actions: singleOrMultiAction(),
                errorMessage: 'An error occurred removing items from the worklist',
              });
            },
            icon: config.isContextMenu ? iconClassToIconElement(button.icon) : iconClassToString(button.icon),
            tooltip: button.tooltip,
          };

        case 'separator':
          return 'separator';

        case 'reviewStyleDetails':
          if (mode === 'singleSelect' && config.params && config.singleSelectParams) {
            return {
              name: button.text,
              action() {
                if (config.params && config.params.node && config.singleSelectParams) {
                  const identity = config.params.node.data[config.identityField];
                  const parentIdentity = config.params.node.data[config.singleSelectParams.parentIdentityField];
                  if (identity && parentIdentity) {
                    if (config.singleSelectParams.type === ContextMenuType.card) {
                      config.singleSelectParams.openStylePane();
                    } else {
                      config.singleSelectParams.openStylePane({
                        id: identity,
                        parentId: parentIdentity,
                      });
                    }
                    config.singleSelectParams.onPostAction?.();
                  }
                }
              },
              icon: config.isContextMenu ? iconClassToIconElement(button.icon) : iconClassToString(button.icon),
              tooltip: button.tooltip,
            };
          }
          return '';
        // return a string here to filter out later.
        // Somehow return null/undefined TypeScript not be able
        // to confirm that undefined values have been fully eliminated.

        default:
          return '';
      }
    })
    .filter((item) => item !== '');

  return worklistConfigButtonsFinal;
};
